import { useThunkDispatch } from '@domain-group/react-hooks'

import * as searchActions from '@redux/modules/search'

import type { LayoutTemplateModel, TaskListModel, TemplatePageModel, TemplatePagePartialModel } from '@domain-group/realhub-backend-api-types'

type EntityParamsType = {
  id?: number,
  project_id?: number,
  type?: string,
}

const generateUrl = (entity: EntityParamsType): (string | undefined | null) => {
  const { id, type } = entity || {}

  if (!id || !type) return null

  if (type === 'LayoutTemplate') return `#/layoutTemplates/${id}/edit`
  if (type === 'TaskList') return `#/projects/${id}/edit`
  if (type === 'TemplatePage') return `#/templates/${id}/edit`
  if (type === 'TemplatePagePartial') return `#/templatePartials/${id}/edit`
}

type Result = {
  tasks: Array<{ id: number, title: string }>,
  templates: Array<object>,
}

const mapResults = (data: any, generateUrlFn: (entity: unknown) => void) => {
  const groupedData = data.reduce((result: Record<string, any[]>, currentValue: any) => {
    result[currentValue.type] = result[currentValue.type] || []
    result[currentValue.type].push(currentValue)

    return result
  }, Object.create(null))

  const {
    LayoutTemplate: layoutTemplates,
    TaskList: taskLists,
    TemplatePage: templatePages,
    TemplatePagePartial: templatePagePartials,
  } = groupedData || {}

  const presentResults = []
  const emptyResults = []

  // Layout Templates
  if (layoutTemplates){
    if (layoutTemplates.length){
      presentResults.push({
        items: layoutTemplates.map((layoutTemplate: LayoutTemplateModel) => ({
          entityType: 'Task',
          href: generateUrlFn(layoutTemplate),
          id: layoutTemplate.id,
          key: layoutTemplate.id,
          subtitle: null,
          title: layoutTemplate.title,
        })),
        title: 'Layout Templates',
      })
    } else {
      emptyResults.push({
        items: [{
          id: -1,
          key: -1,
          subtitle: 'Try searching again',
          title: 'No Layout Templates found',
          priority: 0,
        }],
        title: 'Layout Templates',
      })
    }
  }

  // Task List: Project
  if (taskLists){
    if (taskLists.length){
      presentResults.push({
        items: taskLists.map((taskList: TaskListModel) => ({
          entityType: 'Task',
          href: generateUrlFn(taskList),
          id: taskList.id,
          key: taskList.id,
          subtitle: null,
          title: taskList.title,
        })),
        title: 'Projects',
      })
    } else {
      emptyResults.push({
        items: [{
          id: -1,
          key: -1,
          subtitle: 'Try searching again',
          title: 'No Projects found',
          priority: 0,
        }],
        title: 'Projects',
      })
    }
  }

  // Template Page
  if (templatePages){
    if (templatePages.length){
      presentResults.push({
        items: templatePages.map((templatePage: TemplatePageModel) => ({
          canLogin: false,
          entityType: 'TemplatePage',
          href: generateUrlFn(templatePage),
          id: templatePage.id,
          key: templatePage.id,
          subtitle: null,
          title: templatePage.title,
        })),
        title: 'Template Pages',
      })
    } else {
      emptyResults.push({
        items: [{
          id: -1,
          key: -1,
          subtitle: 'Try searching again',
          title: 'No Template pages found',
          priority: 0,
        }],
        title: 'Template Pages',
      })
    }
  }

  // Template Page Partial
  if (templatePagePartials){
    if (templatePagePartials.length){
      presentResults.push({
        items: templatePagePartials.map((templatePagePartial: TemplatePagePartialModel) => ({
          canLogin: false,
          entityType: 'TemplatePagePartial',
          href: generateUrlFn(templatePagePartial),
          id: templatePagePartial.id,
          key: templatePagePartial.id,
          subtitle: null,
          title: templatePagePartial.title,
        })),
        title: 'Template Page Partials',
      })
    } else {
      emptyResults.push({
        items: [{
          id: -1,
          key: -1,
          subtitle: 'Try searching again',
          title: 'No Template page partials found',
          priority: 0,
        }],
        title: 'Template Page Partials',
      })
    }
  }

  // Final failsafe if no searchEntities were specified
  // User should never see this because DashboardSearch should always be passed searchEntities
  if (!taskLists && !templatePages){
    emptyResults.push({
      items: [{
        id: -1,
        key: -1,
        subtitle: 'Try searching again',
        title: 'No Results found',
        priority: 0,
      }],
      title: 'Results',
    })
  }

  return { groups: [...presentResults, ...emptyResults] }
}

const dashboardSearch = (options: {}, searchEntities: [], dispatch: () => void) => {
  // @ts-expect-error
  const string = options?.string ?? ''

  if (string.length > 2){
    const { dashboardSearch: dashboardSearchFn } = searchActions

    const params = {
      search_entities_by_detail: searchEntities,
      string,
    }

    // @ts-expect-error TS2554
    return dispatch(dashboardSearchFn(params))
  }
  return Promise.resolve({ success: false })
}

type UseDashboardSearch = {
  searchEntities?: Array<any>,
}
const useDashboardSearch = (params: UseDashboardSearch) => {
  const { searchEntities } = params

  // @ts-expect-error TS7006
  const generateUrlFn = entity => generateUrl(entity)

  const dispatch = useThunkDispatch()

  return {
    callbacks: {
      // @ts-expect-error TS2345
      dashboardSearch: (options: {}) => dashboardSearch(options, searchEntities, dispatch),
      mapResults: (data: Result) => mapResults(data, generateUrlFn),
    },
  }
}

export default useDashboardSearch
